/**
 * Ad size parsing and slot sizing utilities
 * @module lib/adSizeUtils
 */

const MOBILE_LB_MAX_WIDTH = 320;
const MOBILE_LB_MAX_HEIGHT = 50;
const MOBILE_LB_SLOT_ID_PATTERN = /^mobile-lb-(?:narcity|mtlblog)-\d+$/;

function isMobileLbSlotId(id) {
  return typeof id === 'string' && MOBILE_LB_SLOT_ID_PATTERN.test(id);
}

function isMobileLbAllowedSize(width, height) {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h)) return false;
  return w <= MOBILE_LB_MAX_WIDTH && h <= MOBILE_LB_MAX_HEIGHT;
}

// Hard CSS clamp so oversized creatives (e.g. AdX all-sizes backfill serving
// 300x250 into a 320x50 slot) can't push page content around. Applied at slot
// setup and reinforced in slotRenderEnded.
function clampMobileLbContainer(element) {
  if (!element || !element.style) return;
  element.style.maxWidth = `${MOBILE_LB_MAX_WIDTH}px`;
  element.style.maxHeight = `${MOBILE_LB_MAX_HEIGHT}px`;
  element.style.overflow = 'hidden';
  element.style.boxSizing = 'border-box';
}

function applyAdSlotBoxSizing(element) {
  if (!element || !element.style) return;
  const isContentAd = Boolean(
    element.classList &&
    typeof element.classList.contains === 'function' &&
    element.classList.contains('content-ad')
  );

  // Content slots can inherit label/presentation padding from the publisher.
  // Keep the declared GPT height available to the creative itself and let that
  // padding grow the outer box. Fixed mobile leaderboards remain hard-clamped.
  element.style.boxSizing =
    isContentAd && !isMobileLbSlotId(element.id) ? 'content-box' : 'border-box';
}

function parseAdSize(size) {
  if (!size) return null;
  if (Array.isArray(size) && size.length >= 2) {
    const width = Number(size[0]);
    const height = Number(size[1]);
    if (!Number.isFinite(width) || !Number.isFinite(height)) {
      return null;
    }
    return { width, height };
  }
  if (typeof size === 'string') {
    const trimmed = size.trim().toLowerCase();
    if (!trimmed || trimmed === 'fluid') {
      return null;
    }
    const match = /(\d+)\s*x\s*(\d+)/.exec(trimmed);
    if (!match) return null;
    return { width: Number(match[1]), height: Number(match[2]) };
  }
  return null;
}

function getPxValue(value) {
  if (!value) return 0;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function ensureAdSlotMinHeight(element, minHeight, minWidth) {
  if (!element || !element.style) return;
  const nextHeight = Number(minHeight);
  if (Number.isFinite(nextHeight) && nextHeight > 0) {
    const current = getPxValue(element.style.minHeight);
    if (nextHeight > current) {
      element.style.minHeight = `${nextHeight}px`;
    }
  }
  const nextWidth = Number(minWidth);
  if (Number.isFinite(nextWidth) && nextWidth > 0) {
    const current = getPxValue(element.style.minWidth);
    if (nextWidth > current) {
      element.style.minWidth = `${nextWidth}px`;
    }
  }
  applyAdSlotBoxSizing(element);
}

function lockAdSlotMinHeight(element, height) {
  if (!element || !element.style) return;
  const nextHeight = Number(height);
  if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;
  let prev = 0;
  if (element.dataset && element.dataset.millaMaxHeight) {
    const parsed = Number(element.dataset.millaMaxHeight);
    if (Number.isFinite(parsed)) {
      prev = parsed;
    }
  }
  const maxHeight = Math.max(prev, nextHeight);
  if (element.dataset) {
    element.dataset.millaMaxHeight = String(maxHeight);
  }
  const current = getPxValue(element.style.minHeight);
  if (maxHeight > current) {
    element.style.minHeight = `${maxHeight}px`;
  }
}

function applyStableAdSlotSize(element, size) {
  if (!element || !element.style) return;
  const parsed = parseAdSize(size);
  if (!parsed) return;
  let { width, height } = parsed;
  if (width <= 1 && height <= 1) return;

  // Cap mobile-lb slots to their declared max. Prevents applyStableAdSlotSize
  // from growing the container when prebid/GPT report an oversized creative.
  if (element.id && isMobileLbSlotId(element.id)) {
    clampMobileLbContainer(element);
    if (width > MOBILE_LB_MAX_WIDTH) width = MOBILE_LB_MAX_WIDTH;
    if (height > MOBILE_LB_MAX_HEIGHT) height = MOBILE_LB_MAX_HEIGHT;
  }

  ensureAdSlotMinHeight(element, height, width);
  lockAdSlotMinHeight(element, height);
  if (typeof element.closest === 'function') {
    const wrapper = element.closest('.ad-wrapper');
    if (wrapper) {
      lockAdSlotMinHeight(wrapper, height);
    }
  }

  const currentHeight = getPxValue(element.style.height);
  if (!currentHeight || currentHeight < height) {
    element.style.height = `${height}px`;
  }
  const currentWidth = getPxValue(element.style.width);
  if (!currentWidth || currentWidth < width) {
    element.style.width = `${width}px`;
  }
  element.style.margin = '0 auto';
}

function applySlotSizeFromPrebid(slotId, pbjs) {
  if (typeof document === 'undefined') return;
  if (!slotId || !pbjs || typeof pbjs.getAdserverTargetingForAdUnitCode !== 'function') {
    return;
  }
  const targeting = pbjs.getAdserverTargetingForAdUnitCode(slotId) || {};
  const size = targeting.hb_size;
  if (!size) return;
  const element = document.getElementById(slotId);
  if (!element) return;
  applyStableAdSlotSize(element, size);
}

module.exports = {
  parseAdSize,
  getPxValue,
  ensureAdSlotMinHeight,
  lockAdSlotMinHeight,
  applyStableAdSlotSize,
  applySlotSizeFromPrebid,
  isMobileLbSlotId,
  isMobileLbAllowedSize,
  clampMobileLbContainer,
  applyAdSlotBoxSizing,
  MOBILE_LB_MAX_WIDTH,
  MOBILE_LB_MAX_HEIGHT
};
